import { Button } from '@foam/buoy/src/components/Button/Button';
import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { IconButton } from '@foam/buoy/src/components/IconButton/IconButton';
import { cn } from '@foam/buoy/src/lib/utils';
import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useRef, useState } from 'react';
import {
  NewMetricCard,
  getReadableMetricValue,
} from '~/components/CreatorChannelMetrics';
import { CursorTooltip } from '~/components/CursorTooltip/CursorTooltip';
import { AgeChart as NewAgeChart } from '~/components/TalentCharts/Age/AgeChart';
import { CountryChart as NewCountryChart } from '~/components/TalentCharts/Country/CountryChart';
import { GenderChart as NewGenderChart } from '~/components/TalentCharts/Gender/GenderChart';
import { FEATURE_FLAGS, SOCIAL_PLATFORMS } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';

import { CreatorProfileCardBuilder } from '../PageBuilder/blocks/CreatorProfileCardBuilder';
import { AgeChart } from '../PageBuilder/blocks/TalentCharts/AgeChart';
import { CountryChart } from '../PageBuilder/blocks/TalentCharts/CountryChart';
import { GenderChart } from '../PageBuilder/blocks/TalentCharts/GenderChart';
import { SingleMetric } from '../PageBuilder/blocks/TalentCharts/SingleMetric';
import { HeadingSection } from '../PageBuilder/blocks/TalentCharts/TalentCharts';
import { ContentCard } from '../PageBuilder/blocks/TalentContent/ContentCard/ContentCard';
import {
  brandLogos,
  creator,
  demographicsData,
  editorState,
  featuredContent,
  metricsData,
  talentCharts,
} from './mocks';

const EditableRichTextView = dynamic(
  () =>
    import('../PageBuilder/blocks/EditableRichTextView').then(
      (c) => c.EditableRichTextView,
    ),
  {
    loading: () => null,
  },
);

// Memoize animation variants to prevent recreating on every render
const popUpVariant = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1 },
} as const;

export const MediaKitsCard = () => {
  const { platform, demographics } = talentCharts;
  const { genders, ages, countries } = demographicsData;
  const containerRef = useRef<HTMLDivElement>(null);
  const [showCursorHelp, setShowCursorHelp] = useState(false);
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);

  const isTalentRecordGrowthRateEnabled = useFeature(
    FEATURE_FLAGS.foamTalentRecordGrowthRate,
  ).isEnabled;

  const isMetricsCardsEnabled = useFeature(
    FEATURE_FLAGS.foamMetricsCards,
  ).isEnabled;

  return (
    <div className={cn({ 'cursor-help': showCursorHelp })}>
      <div
        ref={containerRef}
        className="pointer-events-none flex w-full flex-col gap-10 rounded-24 border-none bg-neutral-0 px-10 pb-10 pt-8 shadow-[0_2px_6px_0_rgba(28,33,40,0.25)]"
      >
        <motion.div
          className="flex items-center justify-between"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0 }}
          variants={popUpVariant}
        >
          <h1 className="text-24 font-500 text-[#000]">
            Brielle Carter’s Media Kit
          </h1>

          <Button
            variant="tertiary"
            size="lg"
            data-tooltip-id="contact-tooltip"
            data-tooltip-title="Direct Contact"
            data-tooltip-content="Make it easy for brands to reach you. With the contact button, your email is shared seamlessly so opportunities come straight to your inbox."
          >
            <Icon name="Envelope" />
            Contact
          </Button>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          variants={popUpVariant}
        >
          <div
            className="transition-opacity duration-300"
            style={{
              opacity: activeTooltipId === 'creator-profile-tooltip' ? 0.7 : 1,
            }}
            data-tooltip-id="creator-profile-tooltip"
            data-tooltip-title="About You"
            data-tooltip-content="Introduce yourself with your name, age, location, bio, verticals, and up-to-date follower count. Add custom links and additional social profiles."
          >
            <CreatorProfileCardBuilder
              mediaKitId="brielle-carter-media-kit"
              creator={creator}
            />
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.2 }}
          variants={popUpVariant}
        >
          <div
            className="transition-opacity duration-300"
            style={{
              opacity: activeTooltipId === 'editable-text-tooltip' ? 0.7 : 1,
            }}
            data-tooltip-id="editable-text-tooltip"
            data-tooltip-title="Custom text"
            data-tooltip-content="Include custom text to expand on your story, include a rate card, or write a personal pitch to a brand."
          >
            <EditableRichTextView
              editorState={JSON.stringify(editorState)}
              id="brielle-carter-media-kit"
              readonly={true}
            />
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
          variants={popUpVariant}
        >
          <div
            data-tooltip-id="talent-charts-tooltip"
            data-tooltip-title="Audience & Performance"
            data-tooltip-content="Display your up-to-date audience demographics and channel metrics featuring real-time data."
            className="flex flex-col gap-12 rounded-12 transition-opacity duration-300"
            style={{
              opacity: activeTooltipId === 'talent-charts-tooltip' ? 0.7 : 1,
            }}
          >
            <div className="flex flex-col gap-4">
              <HeadingSection
                platform={platform as SOCIAL_PLATFORMS}
                title="Instagram Audience Demographics"
              />
              <div className="relative">
                <div className="grid grid-cols-[repeat(auto-fill,minmax(338px,1fr))] items-stretch gap-6">
                  {demographics?.includes('gender') && (
                    <div>
                      {isTalentRecordGrowthRateEnabled ? (
                        <NewGenderChart
                          genders={[
                            {
                              label: 'Male',
                              value: (genders?.male ?? 0) / 100,
                            },
                            {
                              label: 'Female',
                              value: (genders?.female ?? 0) / 100,
                            },
                            {
                              label: 'Other',
                              value: (genders?.other ?? 0) / 100,
                            },
                          ]}
                        />
                      ) : (
                        <GenderChart
                          male={genders?.male}
                          female={genders?.female}
                          other={genders?.other}
                        />
                      )}
                    </div>
                  )}

                  {demographics?.includes('age') && (
                    <div>
                      {isTalentRecordGrowthRateEnabled ? (
                        <NewAgeChart
                          ages={ages?.map((a) => ({
                            label: a.age,
                            value: a.count / 100,
                          }))}
                        />
                      ) : (
                        <AgeChart ages={ages} />
                      )}
                    </div>
                  )}

                  {demographics?.includes('country') && (
                    <div>
                      {isTalentRecordGrowthRateEnabled ? (
                        <NewCountryChart
                          countries={countries?.map((c) => ({
                            label: c.country,
                            value: c.value / 100,
                            meta: { countryName: c.country },
                          }))}
                        />
                      ) : (
                        <CountryChart countries={countries} />
                      )}
                    </div>
                  )}
                </div>
                <span className="text-12 font-400 text-neutral-300">
                  Last updated: 4 hours ago
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <HeadingSection
                  platform={platform as SOCIAL_PLATFORMS}
                  title="Instagram Talent & Channel metrics"
                />

                <span className="cursor-pointer text-12 font-400 text-blue-2-500">
                  Learn more about metrics
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {metricsData.map((metric) =>
                  isMetricsCardsEnabled ? (
                    <NewMetricCard
                      key={metric.title}
                      metricTitle={metric.title}
                      metricSubtitle={metric.subtitle}
                      metricValue={metric.value}
                      metricTransformValue={
                        metric.transformValue as 'number' | 'percentage'
                      }
                    />
                  ) : (
                    <SingleMetric
                      key={metric.title}
                      title={metric.title}
                      value={
                        getReadableMetricValue(
                          metric.value,
                          metric.transformValue as 'number' | 'percentage',
                        ) || '-'
                      }
                    />
                  ),
                )}
              </div>
              <span className="text-12 font-400 text-neutral-300">
                Last updated: 6 hours ago
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="flex flex-col gap-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.4 }}
          variants={popUpVariant}
        >
          <div className="flex flex-col gap-1">
            <h1 className="text-32 font-500 text-neutral-700">
              Brand Partnerships
            </h1>
            <p className="text-16 font-400 text-neutral-700">
              Here are a few brands I've collaborated with recently.
            </p>
          </div>
          <div
            data-tooltip-id="brand-partnerships-tooltip"
            data-tooltip-title="Brand Partnerships"
            data-tooltip-content="Easily feature your past partnerships using our logo gallery."
            className="w-full flex-wrap justify-center gap-4 transition-opacity duration-300"
            style={{
              opacity:
                activeTooltipId === 'brand-partnerships-tooltip' ? 0.7 : 1,
            }}
          >
            <div className="flex flex-wrap justify-center gap-4">
              {brandLogos.map((logo, idx) => (
                <div
                  key={`${logo.brandName}-${idx}`}
                  className="flex size-40 items-center justify-center rounded-8 px-2"
                >
                  <img
                    src={logo.url}
                    alt={logo.brandName}
                    className="size-auto max-h-20 max-w-32 object-contain"
                    loading="lazy"
                    draggable={false}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center text-neutral-600 font-medium text-sm text-center px-2">
                        ${logo.brandName}
                      </div>
                    `;
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          className="flex flex-col gap-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.5 }}
          variants={popUpVariant}
        >
          <h1 className="text-32 font-500 text-neutral-700">
            Featured Content
          </h1>
          <div
            data-tooltip-id="featured-content-tooltip"
            data-tooltip-title="Featured Content"
            data-tooltip-content="Showcase your top-performing posts across your social platforms, with the option to include their content metrics."
            className="grid grid-cols-10 gap-4 transition-opacity duration-300"
            style={{
              opacity: activeTooltipId === 'featured-content-tooltip' ? 0.7 : 1,
            }}
          >
            <div className="col-span-full grid grid-cols-10 gap-4">
              {featuredContent.map((content, index) => (
                <div
                  key={content.id}
                  className={index === 0 ? 'col-span-4' : 'col-span-3'}
                >
                  <ContentCard content={content as any} showMetrics={true} />
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.6 }}
          variants={popUpVariant}
        >
          <div
            data-tooltip-id="embedded-videos-tooltip"
            data-tooltip-title="Embedded Videos"
            data-tooltip-content="Embed playable videos from YouTube, Vimeo, Google Drive and more right into your Media Kit."
            className="relative w-full transition-opacity duration-300"
            style={{
              opacity: activeTooltipId === 'embedded-videos-tooltip' ? 0.7 : 1,
            }}
          >
            <img
              src="/images/content_video.png"
              alt="Content video thumbnail"
              className="h-auto w-full rounded-12"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <IconButton
                variant="primary"
                aria-label="Play video"
                size="xl"
                icon="Play"
              />
            </div>
          </div>
        </motion.div>

        {/* Global Tooltip */}
        <CursorTooltip
          containerRef={containerRef}
          onVisibilityChange={setShowCursorHelp}
          onActiveTooltipChange={setActiveTooltipId}
        />
      </div>
    </div>
  );
};
